import { Stack, Group, Button, Paper, Text, List } from '@mantine/core'
import type { AuthorizationFormState } from './authorizationFormData'
import { formatFacilityAddress } from './authorizationFormData'
import { AuthorizationStatusView } from './AuthorizationStatusBadge'
import type { AuthorizationStatus } from './useAuthorizationStatus'

type Props = {
  values: AuthorizationFormState
  status: AuthorizationStatus
  statusLoading: boolean
  signed: boolean
  onBack: () => void
  onPreview: () => void
  previewing: boolean
  onSendFax: () => void
  sending: boolean
}

export const ReviewStep = ({
  values,
  status,
  statusLoading,
  signed,
  onBack,
  onPreview,
  previewing,
  onSendFax,
  sending,
}: Props) => {
  const hasFax = Boolean(values.facility.fax)
  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={600}>Review request</Text>
          <AuthorizationStatusView status={status} loading={statusLoading} />
        </Group>
        <Text size="sm">
          <b>Facility:</b> {values.facility.name || '—'}
        </Text>
        <Text size="sm">
          <b>Fax:</b> {values.facility.fax || '—'}
        </Text>
        <Text size="sm">
          <b>Address:</b> {formatFacilityAddress(values.facility) || '—'}
        </Text>
        <Text size="sm" mt="xs">
          <b>Records:</b>
        </Text>
        <List size="sm">
          {values.recordTypes.map((r) => (
            <List.Item key={r}>{r}</List.Item>
          ))}
        </List>
        <Text size="sm" mt="xs">
          <b>Date range:</b> {values.dateRangeStart || '—'} – {values.dateRangeEnd || '—'}
        </Text>
        <Text size="sm">
          <b>Purpose:</b> {values.purpose || '—'}
        </Text>
      </Paper>
      {!signed && (
        <Text size="sm" c="orange">
          The patient has not signed the authorization yet. You can preview the form, but it cannot
          be sent until the patient signs.
        </Text>
      )}
      {signed && !hasFax && (
        <Text size="sm" c="orange">
          The selected facility has no fax number on file. Add one on the Facility step before
          sending.
        </Text>
      )}
      <Group justify="space-between" pb="md">
        <Button variant="default" onClick={onBack} disabled={sending}>
          Back
        </Button>
        <Group gap="sm">
          <Button variant="outline" onClick={onPreview} loading={previewing} disabled={sending}>
            Generate PDF Preview
          </Button>
          <Button
            color="teal"
            onClick={onSendFax}
            loading={sending}
            disabled={!signed || !hasFax}
            title={
              !signed
                ? 'Patient signature required before sending'
                : !hasFax
                  ? 'The selected facility has no fax number'
                  : undefined
            }
          >
            Send by Fax
          </Button>
        </Group>
      </Group>
    </Stack>
  )
}
